/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { Tenant } from "../models/http/tenant/tenant.model";

@Injectable({
	providedIn: "root"
})
export class StorageService {
	constructor() {}

	/**
	 * Stores the provided tenants array in the local storage.
	 *
	 * @param tenants - An array of Tenant objects to be stored.
	 */
	public setTenants(tenants: Tenant[]) {
		localStorage.setItem("tenants", JSON.stringify(tenants));
	}

	/**
	 * Retrieves the list of tenants from local storage.
	 *
	 * @returns An array of tenants if found in local storage, otherwise an empty array.
	 */
	public getTenants() {
		const tenants = localStorage.getItem("tenants");

		return tenants ? (JSON.parse(tenants) as Tenant[]) : [];
	}

	/**
	 * Retrieves the tenant ID from the local storage.
	 *
	 * @returns The tenant ID if it exists in local storage, otherwise an empty string.
	 */
	public getTenantId(): string {
		const tenants = this.getTenants();
		return !tenants.length ? "" : tenants[0].tenantId;
	}

	/**
	 * Stores the user's role in the local storage.
	 *
	 * @param role - The role of the user to be stored.
	 */
	public setUserRole(role: string) {
		localStorage.setItem("role", role);
	}

	/**
	 * Retrieves the user's role from local storage.
	 *
	 * @returns The user's role if it exists in local storage, otherwise null.
	 */
	public getUserRole(): string | null {
		return localStorage.getItem("role");
	}

	/**
	 * Retrieves the application's accessibility state from local storage.
	 *
	 * @returns {boolean} - Returns `true` if the "isAccessible" key in local storage is set to "true",
	 *                      otherwise returns `false`.
	 */
	public getApplicationAccessibilityState(): boolean {
		return localStorage.getItem("isAccessible") === "true";
	}

	/**
	 * Stores the ServiceNow enablement flag in local storage.
	 *
	 * @param isEnabled - Indicates whether ServiceNow is enabled for the current user.
	 */
	public setServiceNowEnabled(isEnabled: boolean) {
		localStorage.setItem("snEnabled", isEnabled.toString());
	}

	/**
	 * Retrieves the ServiceNow enablement flag from local storage.
	 *
	 * @returns `true` if ServiceNow is enabled, otherwise `false`.
	 */
	public getServiceNowEnabled(): boolean {
		return localStorage.getItem("snEnabled") === "true";
	}

	/**
	 * Sets the application's accessibility state in the local storage.
	 *
	 * @param isAccessible - A boolean value indicating whether the application is in an accessible state.
	 *                       `true` for accessible mode, `false` otherwise.
	 */
	public setApplicationAccessibilityState(isAccessible: boolean) {
		localStorage.setItem("isAccessible", isAccessible.toString());
	}
}
